/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class DashboardContentService
 * @hideconstructor
 *
 * @classdesc
 * CA dashboard content service oprations: getAncestors, onSaveAsSuccess, Save, SaveAs, SaveThumbnail
 */
define([
	'../../dashboard/glass/util/SaveActionUtil',
	'../../app/util/GlassUtil',
	'../../app/nls/StringResources',
	'../../dashboard/loader/DeploymentReferencesUtil'
], function (
	SaveActionUtil,
	GlassUtil,
	StringResources,
	DeploymentReferencesUtil
) {
	return class DashboardContentService {

		constructor() { }

		initialize( glassContext ) {
			this._glassContext = glassContext;
			return Promise.resolve();
		}


		/**
		 * Given a dashboard board Id or Url, return the dashboard's parent path information (Ancestors)
		 * @async
		 * @param {Object} spec - JSON object that is used to locate a dashboard object
		 * @param {string} spec.id - The dashboard board id
		 * @param {string} spec.url - url of the dashboard. Used only when avaiable otherwise spec.id is used
		 * @return {Promise<Array|null>} - Resolves with an array of parent path ancestors or null when no id or an error occurs.
		 */
		getAncestors(spec = {}) {
			const { id, url } = spec;
			if (!id && !url) {
				return Promise.resolve(null);
			} else {
				return this._getContentService()
					.then((oContentService) => {
						const server_URL = url || oContentService.getBaseObjectsURL() + '/' + id;
						const options = { data: { fields: 'ancestors' } };
						return oContentService.get(server_URL, options);
					}).then((result) => {
						return Promise.resolve(result ? (result.data[0].ancestors || null) : null);
					}).catch((error) => {
						this._logError('Error retrieving ancestors:', error);
						return null;
					});
			}
		}

		/**
		 * Given the id or url of a dashboard, return the dashboard information
		 * @param {String} boardId - dashboard Id
		 * @returns {Promise<Object>} - Resolved dashboard information { id, name, searchPath, permissions, boardSpec }
		 */
		getDashboard(boardId) {
			return this._glassContext.getCoreSvc('.Config').getConfigValue('LibertyModeOnly').then((libertyMode) => {
				if (libertyMode === 'true') {
					const url = 'v1/objects/' + boardId;
					const options = {
						dataType: 'json',
						data: {
							'fields': [
								'defaultName',
								'specification',
								'searchPath',
								'deploymentReferences.ancestors',
								'deploymentReferences.defaultName',
								'deploymentReferences.id',
								'deploymentReferences.type',
								'deploymentReferences.searchPath'
							].join(',')
						},
						MRUInfo: {}
					};
					return this._getBoardSpecFromContentSvc(url, options)
						.then(response => this._handleBoardSpecResponse(response));
				} else {
					return this._getBoardSpecFromDashboardServer(boardId)
						.then(response => this._handleBoardSpecResponse(response));
				}
			});
		}

		/**
		 * Fetches the board spec using the content service.
		 * Wrapping the Content Service JQuery deferred in a native Promise to obtain some predictable error handling
		 * @param {String} url - URL of dashboard resource to fetch
		 * @param {String} options - Options to pass into the get request
		 * @return {Promise} - Resolves once the board spec is fetched. Rejects when fails with the error status, code, and messages
		 */
		_getBoardSpecFromContentSvc(url, options) {
			return new Promise((resolve, reject) => {
				this._getContentService()
					.then((contentSvc) => {
						contentSvc.get(url, options)
							.done(resolve)
							.fail((ajaxdfd, jqXHR, textStatus) => {
								reject({
									status: jqXHR.status,
									code: textStatus,
									message: StringResources.get('noDashboardFound')
								});
							});
					});
			});
		}

		/**
		* Fetches the board spec using the dashboard server api
		* Wrapping the Dashboard Server call in a native Promise to obtain some predictable error handling
		* @param {String} boardId - dashboard Id
		* @return {Promise} - resolves when the board spec is fetched. rejects when it fails with the error status, code, and messages
		*/
		_getBoardSpecFromDashboardServer(boardId) {
			return new Promise((resolve, reject) => {
				const ajaxSvc = this._glassContext.getCoreSvc('.Ajax');
				ajaxSvc.ajax({
					url: 'v1/dashboard/specs/' + boardId,
					type: 'GET',
					contentType: 'application/json'
				})
					.done(resolve)
					.fail((ajaxError) => {
						reject({
							status: ajaxError.jqXHR.status,
							code: ajaxError.textStatus,
							message: StringResources.get('noDashboardFound')
						});
					});
			});
		}

		handlePrefetchedDashboard(promise) {
			return promise.then(response => this._handleBoardSpecResponse(response));
		}

		/**
		 * Callback of Save As success
		 * Given a dashboard board id or a dashboard url, return the dashboard spec
		 * @async
		 * @param {Object} spec - JSON object that is used to locate a dashboard object
		 *	@param {string} spec.id - the dashboard board id
		 *	@param {string} spec.url - url of the dashboard which is used only when avaiable otherwise spec.id is used
		 * @return {Promise} - resolved with dashboard information of permissions and boardId
		 * @example
		 * 	permissions is an array that contains elements such as "execute", "read", "setPolicy", "traverse", "write"
		 */
		onSaveAsSuccess(spec = {}) {
			const { id, url } = spec;
			if (!id && !url) {
				return Promise.resolve(null);
			} else {
				return this._getContentService()
					.then((oContentService) => {
						const server_URL = url || (oContentService.getBaseObjectsURL() + '/' + id);
						const oGetOptions = {
							dataType: 'json',
							data: {
								'fields': 'defaultName,specification'
							},
							MRUInfo: {}
						};
						return oContentService.get(server_URL, oGetOptions);
					}).then( (response)=> {
						const data = response && response.data && response.data.length >0 ? response.data[0]: {};
						return Promise.resolve ({
							permissions: data.permissions,
							id: data.id
						});
					});
			}
		}

		/**
		* Update an existing Dashboard object
		* @async
		* @param {DashboardAPI} dashboard api
		* @return {Promise<Object>} Resolved object is the saved spec
		* @example  when failed, the promise will reject an XHR request object
					{
						readyState: 4
						getResponseHeader: ƒ (e)
						getAllResponseHeaders: ƒ ()
						setRequestHeader: ƒ (e,t)
						overrideMimeType: ƒ (e)
						statusCode: ƒ (e)
						..
					}
		*
		*/
		saveDashboard(dashboard, assetId) {
			const spec = dashboard.getFeature('Serializer').toJSON();
			const boardModel = dashboard.getFeature('internal').getBoardModel();

			SaveActionUtil.cleanUnusedCustomColors(dashboard, spec, boardModel);

			const cmSpec = this._getCMSpec(spec, dashboard);

			return new Promise((resolve, reject)=> {
				this._getContentService().then((oContentService) => {
					return oContentService.put(`v1/objects/${assetId}?ignoreInvalidObjectReference=true`, {
						contentType: 'application/json',
						processData: false,
						dataType: 'application/json',
						data: JSON.stringify(cmSpec),
						MRUInfo: {} //A necessary property to support MRU
					}).fail((deferred, request)=>{
						reject(request);
					}).then(()=> {
						resolve({ 'savedSpec': cmSpec.specification, 'status': 'success' });
					});
				});
			});
		}

		/**
		 * CREATE a new Dashboard object
		 * @async
		 * @param {DashboardAPI} dashboard api
		 * @param {string} name of the dashboard
		 * @param {string} url of the dashboard's parent
		 * @param {boolean} True means save as an existing dashboard
		 * @return {Promise<Object>}
		 * @exmaple when failed, the promise rejects an error object like below
		 * 			{
						'status': 'fail',
						'saveRequest': request,
						'cmSpec': cmSpec
					}
		 */
		saveAsDashboard(dashboard, name, url, overwrite) {
			const ajaxService = GlassUtil.getAjaxService(this._glassContext);

			//enable the 'more' button containing the set as home if it is not already enabled
			const moreButton = this._glassContext.appController.findPlugin('com.ibm.bi.glass.common.operations');
			if (moreButton && !moreButton.isEnabled) {
				moreButton.enable();
			}
			return SaveActionUtil.saveAs({ ajaxService, name, url, dashboard, overwrite });
		}

		/**
		 * Save the thumbnail image into a Databoard Object
		 * @async
		 * @param {Object} spec
		 *			spec.thumbnailDataUri - base64 encoded PNG
		 *			spec.id - board Id of a dashboard
		 * @return {Promise<Object>}
		 */
		saveThumbnail(spec={}) {
			const { thumbnailDataUri, id } = spec;
			const dataUriParts = thumbnailDataUri && thumbnailDataUri.split(',');
			if (!dataUriParts || dataUriParts.length != 2) {
				this._logError('Invalid thumbnail returned from Thumbnail service.');
				return;
			}
			if(!id){
				this._logError('Dashboard id is undefined when generating thumbnail.');
				return;
			}
			return this._getContentService().then((oContentService) => {
				return this._getAjaxService().post(
					oContentService.getBaseObjectsURL() + '/' + id + '/items?updateAction=update',
					{
						contentType: 'application/json',
						processData: false,
						dataType: 'text',
						data: JSON.stringify({
							defaultName: 'thumbnail',
							type: 'graphic',
							dataType: 'image/png',
							data: dataUriParts[1]
						})
					});
			});
		}

		/**
		* Process board spec response from dashboard server.
		* @private
		* @param {Object} response - The response of a successful board spec request to dashboard server
		* @return {Promise<Object>} - Resolved with a JSON object that contains the dashboard informaiton
		* @example
			{
				boardSpec: {xxx},
				boardId: 'i12345678',
				permissions: ['execute','read','setPolicy','traverse', 'write'],
				searchPath: 'CAMID("LDAP:u:uid=hmiller,ou=people")/folder[@name=\'My Folders\']/exploration[@name=\'New dashboard1\']',
				name: 'New dashboard1'
			}
		*/
		_handleBoardSpecResponse(response) {
			//No response data
			if (response.data.length === 0) {
				return Promise.reject({
					code: 'notFound',
					message: StringResources.get('noDashboardFound')
				});
			}
			//No read and execute permissions
			const permissions = response.data[0].permissions;
			if (!this._hasPermission(permissions)) {
				return Promise.reject({
					code: 'noPermission',
					message: StringResources.get('noDashboardPermission')
				});
			}
			// should never have a dashboard with null spec... but just in case
			const responseData = response.data[0];
			const specAsString = responseData.specification || '{}';
			const boardSpec = JSON.parse(specAsString);
			boardSpec._meta = {
				bundleID: responseData.id
			};
			//Update deployment reference
			DeploymentReferencesUtil.updateIds(boardSpec, responseData.deploymentReferences, responseData.searchPath);
			boardSpec.features = boardSpec.features || {};
			boardSpec.features.ContentStoreReferences = responseData.deploymentReferences;
			return Promise.resolve({
				boardSpec: boardSpec,
				boardId: responseData.id,
				permissions: responseData.permissions,
				searchPath: responseData.searchPath,
				name: responseData.defaultName
			});
		}

		_hasPermission (permissions) {
			if(Array.isArray(permissions) && permissions.length){
				return ['read', 'execute'].every(permission => permissions.includes(permission));
			}
			return false;
		}

		/**
		 * This function is overriden in StoryContentSerivce
		 * @private
		 * @param {Object} spec - board spec of a dashboard
		 * @return the dashboard CM spec
		 */
		_getCMSpec (spec, dashboard) {
			const boardModel = dashboard.getFeature('internal').getBoardModel();
			return SaveActionUtil.getCMSpec(spec, boardModel, false);
		}

		_logError(errMsg){
			this._glassContext.getCoreSvc('.Logger').error(errMsg);
		}

		_getContentService() {
			return this._glassContext.getSvc('.Content');
		}

		_getAjaxService() {
			return this._glassContext.services.ajax;
		}

		_getDashboardApi(){
			return this._getCurrentView().getDashboardApi();
		}

		_getCurrentView() {
			return this._glassContext.appController.currentAppView.currentContentView;
		}
	};
});
